/* eslint-disable object-property-newline */
import { GlobalState, UI, UserSettings } from '@leverege/ui-redux';
import { Config } from '@leverege/plugin';
import { ValuesCache } from '@leverege/value-cache';
import StringUtil from '@leverege/string-util';
import I18N from '@leverege/i18n';
import { Blueprints } from '@leverege/api-attributes';
import DefaultLayoutDelegate from "./DefaultLayoutDelegate.js";
let defaultLayoutDelegate = DefaultLayoutDelegate;
const LM = '.LayoutModel';
const PLM = '.PreferredLayout';
const {
  t
} = I18N.ns('molten.dataViewers.layout.UserSettingsLayoutDelegate');
const US_OPTS = {
  returnPromise: false
};
const EMPTY = [];
const sParams = {
  type: 'sharedLayout',
  value: 'shared'
};
const uParams = {
  type: 'userLayout',
  value: 'user'
};
const caches = new ValuesCache();

/**
 * 
 * @param {*} opts 
 * @param {String} [opts.client] The client intent string.
 * @param {String} [opts.clientType] The specific type of client. This is used to generate the save
 * location of the layouts. This is also used in conjuntion with path to store the last used layout into
 * localeStorage. If null, client will be used.
 * @param {String} [opts.path] The path of the client
 * @param {String} [opts.objectType] The objectType being shown by the client
 * @param {String} [opts.layoutId] If present, this is the desired layoutId. Otherwise, window.location is used

 * @returns An object containing information about the current layout. It will have 
 * { layoutVersions, layoutGroup, layoutVersion, layout, layoutId, customSettingsPath, 
 * saveLayout, deleteLayout, renameLayout }. If layout is 
 * not present in the layoutVersions, the layoutGroup and layoutVersion and layoutId will be null. In
 * this case, customSettingsPath should be populated with the location where the user's default Layout
 * is saved. 
 */
function use(options, config) {
  const cfg = config || Config.get('UserSettingsLayoutDelegate', null) || {};
  const state = GlobalState.get();
  const {
    client,
    clientType,
    path,
    objectType,
    layoutId: lId
  } = options;
  const {
    usePersona = true,
    useShared = true,
    useObjectType = true,
    useUser = true,
    queryKey = 'v'
  } = cfg;
  const type = clientType || client;
  const key = `${type}.${path}`;
  const activePersona = UI.get(state, 'activePersona');
  const persona = activePersona ? activePersona.name : null;
  const customSettingsPath = key + LM;
  const userBase = useUser && (objectType ? `${type}.t.${objectType}` : `${type}.p.${path}`);
  const sharedBase = useShared ? `${type}.p.${path}` : null;
  const personaBase = usePersona && persona ? `${type}.r.${persona}.${path}` : null;
  const objectTypeBase = useObjectType && objectType ? `${type}.t.${objectType}` : null;
  let tVersions = objectTypeBase ? UserSettings.getSharedVersions(state, objectTypeBase + LM, US_OPTS)?.items : null;
  let pVersions = personaBase ? UserSettings.getSharedVersions(state, personaBase + LM, US_OPTS)?.items : null;
  let sVersions = sharedBase ? UserSettings.getSharedVersions(state, sharedBase + LM, US_OPTS)?.items : null;
  let uVersions = userBase ? UserSettings.getUserVersions(state, userBase + LM, US_OPTS)?.items : null;
  let customLayout = userBase ? UserSettings.get(state, customSettingsPath, US_OPTS) : null;
  const tPreferred = userBase ? UserSettings.getSharedVersion(state, objectTypeBase + PLM, 'id', US_OPTS)?.id : null;
  const pPreferred = userBase ? UserSettings.getSharedVersion(state, personaBase + PLM, 'id', US_OPTS)?.id : null;
  const sPreferred = userBase ? UserSettings.getSharedVersion(state, sharedBase + PLM, 'id', US_OPTS)?.id : null;
  const uPreferred = userBase ? UserSettings.getUserVersion(state, userBase + PLM, 'id', US_OPTS)?.id : null;
  if (tVersions && typeof tVersions.then === 'function') {
    tVersions = EMPTY;
  }
  if (pVersions && typeof pVersions.then === 'function') {
    pVersions = EMPTY;
  }
  if (sVersions && typeof sVersions.then === 'function') {
    sVersions = EMPTY;
  }
  if (uVersions && typeof uVersions.then === 'function') {
    uVersions = EMPTY;
  }
  if (customLayout && typeof customLayout.then === 'function') {
    customLayout = null;
  }

  // Note, this args need to be aligned with createLayoutVersions args
  const layoutVersions = caches.calc(key, createLayoutVersions, pVersions, sVersions, tVersions, uVersions, pPreferred, sPreferred, tPreferred, uPreferred, activePersona, objectType, personaBase, sharedBase, objectTypeBase, userBase, key);
  const layoutId = lId || new URLSearchParams(window.location.search).get(queryKey);
  if (layoutId === 'none' && customLayout) {
    return {
      layoutVersions,
      layoutId,
      layout: customLayout,
      customSettingsPath,
      saveLayout,
      deleteLayout,
      renameLayout
    };
  }
  const entry = getLayoutFrom(layoutVersions, layoutId);
  if (entry === null) {
    const dv = getDefaultLayout({
      ...options,
      layoutVersions,
      activePersona,
      customLayout,
      personaBase,
      sharedBase,
      objectTypeBase,
      userBase,
      customSettingsPath,
      key,
      layoutId
    }, cfg) || {};
    window.localStorage.setItem(`${key}.lastLayoutKey`, dv.layoutId);
    return {
      ...dv,
      layoutVersions,
      customSettingsPath,
      saveLayout,
      deleteLayout,
      renameLayout
    };
  }
  window.localStorage.setItem(`${key}.lastLayoutKey`, entry.layoutId);
  return {
    ...entry,
    layoutVersions,
    customSettingsPath,
    saveLayout,
    deleteLayout,
    renameLayout
  };
}
function getDefaultLayout(options, cfg) {
  if (defaultLayoutDelegate) {
    return defaultLayoutDelegate(options, cfg);
  }
  // const { layoutId } = options
  return {
    layoutId: null,
    layout: null
  };
}
function createLayoutVersions(pVersions, sVersions, tVersions, uVersions, pPreferred, sPreferred, tPreferred, uPreferred, aPersona, objectType, personaBase, sharedBase, objectTypeBase, userBase, key) {
  const lv = [];
  const persona = aPersona ? aPersona.name : null;
  const personaName = aPersona ? aPersona.displayName || aPersona.name : null;
  const blueprint = Blueprints.getByType(objectType);
  if (pVersions) {
    const pParams = {
      type: 'personaLayout',
      name: personaName,
      useName: t('anyPersonaOfType', {
        personaName
      }),
      value: persona,
      path: personaBase,
      preferred: pPreferred
    };
    lv.push(createGroup(pVersions, pParams, `p-${persona}:`, {
      persona
    }));
  }
  if (sVersions) {
    lv.push(createGroup(sVersions, {
      ...sParams,
      useName: t('anyUser'),
      name: t('page'),
      path: sharedBase,
      preferred: sPreferred
    }, 's:'));
  }
  if (tVersions) {
    const cName = blueprint?.name || StringUtil.capitalize(objectType);
    const pParams = {
      type: 'objectTypeLayout',
      name: cName,
      useName: t('anyUserOnPage', {
        cName
      }),
      value: objectType,
      path: objectTypeBase,
      preferred: tPreferred
    };
    lv.push(createGroup(tVersions, pParams, `t-${objectType}:`, {
      objectType
    }));
  }
  if (uVersions) {
    const cName = blueprint?.name || StringUtil.capitalize(objectType);
    const useName = objectTypeBase ? t('meOnAnyPage', {
      cName
    }) : t('me');
    lv.push(createGroup(uVersions, {
      ...uParams,
      name: t('mine'),
      useName,
      path: userBase,
      preferred: uPreferred
    }, 'u:'));
  }
  return lv;
}
function createGroup(items, grpParams, prefix, vParams) {
  const children = (items || EMPTY).map(version => {
    return {
      ...vParams,
      value: prefix + version.name,
      localValue: version.name,
      name: version?.data?.name || version.name,
      source: version,
      prefix
    };
  });
  return {
    ...grpParams,
    prefix,
    source: items,
    children
  };
}

/**
 * Returns the LayoutModel from the layout groups
 * @param {Array<LayoutGroups>} layoutVersions an array of LayoutGroups so look into 
 * @param {String} value the unique name of the layout being used 
 * @returns Object containing the layoutGroup, layoutVersion, layout and layoutId for the given value, or null
 * if it was not found
 */
function getLayoutFrom(layoutVersions, value) {
  if (value == null || layoutVersions == null) {
    return null;
  }
  for (let cn = 0; cn < layoutVersions.length; cn++) {
    const group = layoutVersions[cn];
    const version = group.children?.find(c => c.value === value);
    if (version) {
      return {
        layoutId: value,
        layoutGroup: group,
        layoutVersion: version,
        layout: version?.source?.data
      };
    }
  }
  return null;
}
function getVersionFrom(layoutGroup, value, valueIsLocal = false) {
  if (value == null || layoutGroup == null) {
    return null;
  }
  const name = valueIsLocal ? rsrcName(value) : value;
  const v = layoutGroup.children?.find(c => {
    return valueIsLocal !== true ? c.value === name : c.localValue === name;
  });
  if (v) {
    return v?.source?.data;
  }
  return null;
}
function getVersionValue(group, name) {
  const rName = rsrcName(name);
  return group.prefix + rName;
}
async function saveLayout(evt) {
  const {
    layoutGroup: group,
    name,
    value,
    saveToCustomLayout,
    customSettingsPath
  } = evt;
  if (saveToCustomLayout) {
    await UserSettings.set(customSettingsPath, evt.value);
    return {
      value: null,
      localValue: null,
      prefix: null,
      source: {
        data: evt.value
      }
    };
  }
  const rName = rsrcName(name);
  const nMdl = {
    ...value,
    name
  };
  const path = group.path + LM;
  switch (group.type) {
    case 'userLayout':
      await UserSettings.setUserVersion(path, rName, nMdl);
      break;
    case 'sharedLayout':
    case 'personaLayout':
    case 'objectTypeLayout':
      await UserSettings.setSharedVersion(path, rName, nMdl);
      break;
    default:
      throw new Error(t('error', {
        action: 'saveLayout'
      }));
  }
  return {
    value: group.prefix + rName,
    localValue: rName,
    prefix: group.prefix,
    name,
    source: {
      data: nMdl
    }
  };
}
function deleteLayout(evt) {
  const {
    layoutGroup: group,
    layoutVersion: version
  } = evt;
  const path = group.path + LM;
  switch (group.type) {
    case 'userLayout':
      return UserSettings.resetUserVersion(path, version.localValue);
    case 'sharedLayout':
    case 'personaLayout':
    case 'objectTypeLayout':
      return UserSettings.resetSharedVersion(path, version.localValue);
    default:
      throw new Error(t('error', {
        action: 'deleteLayout'
      }));
  }
}
async function renameLayout(evt) {
  const {
    layoutGroup: group,
    layoutVersion: version,
    newName
  } = evt;
  const rName = rsrcName(newName);
  const path = group.path + LM;
  let r = null;
  switch (group.type) {
    case 'userLayout':
      await UserSettings.renameUserVersion(path, version.localValue, rName);
      r = await UserSettings.setUserVersion(path, rName, {
        ...version.source.data,
        name: newName
      });
      break;
    case 'sharedLayout':
    case 'personaLayout':
    case 'objectTypeLayout':
      await UserSettings.renameSharedVersion(path, version.localValue, rName);
      r = await UserSettings.setSharedVersion(path, rName, {
        ...version.source.data,
        name: newName
      });
      break;
    default:
      throw new Error(t('error', {
        action: 'renameLayout'
      }));
  }
  return {
    ...version,
    value: version.prefix + rName,
    localValue: rName,
    name: newName,
    source: {
      ...version.source,
      data: r
    }
  };
}
function setPreferredLayout(evt) {
  const {
    layoutGroup: group,
    value
  } = evt;
  const path = group.path + PLM;
  switch (group.type) {
    case 'userLayout':
      return UserSettings.setUserVersion(path, 'id', {
        id: value
      });
    case 'sharedLayout':
    case 'personaLayout':
    case 'objectTypeLayout':
      return UserSettings.setSharedVersion(path, 'id', {
        id: value
      });
    default:
      throw new Error(t('error', {
        action: 'setPerferredLayout'
      }));
  }
}
function rsrcName(name) {
  return name?.replace(/[ &*^%@!]/g, '');
}

/**
 * A function to return the layoutId to use for the default view or when the 
 * specified one does not exist.
 * @param {Function} a function( { state, props, opts, layoutVersions, activePersona, 
 * layoutId, key } ) If layoutId is null, it means that this is the default view. If it
 * non-null, it means that the version of the layout does not exist. Key is the unique 
 * key for the layout, and activePersona is the current user role. state, props and opts
 * are the same arguemnts given to use(). This function should return the { layoutId, layout } 
 * to use. 
 */
function setDefaultLayoutDelegate(d) {
  defaultLayoutDelegate = d;
}
function getDefaultLayoutDelegate() {
  return defaultLayoutDelegate;
}
export default {
  use,
  saveLayout,
  deleteLayout,
  renameLayout,
  setPreferredLayout,
  getLayoutFrom,
  getVersionFrom,
  getVersionValue,
  setDefaultLayoutDelegate,
  getDefaultLayoutDelegate
};